"use client";

import type * as React from "react";
import { cn } from "../../lib/utils";
import { Badge } from "./badge";

export type CountStickerProps = {
  /** The count, already formatted. The HOST'S accessible name carries it (a
   *  tile's or chip's `aria-label`); the sticker itself is presentation. */
  count?: string;
  /** A glyph in place of digits (an icon that marks the host's state). */
  children?: React.ReactNode;
  /**
   * Paint the disc in `primary-foreground` and the digit in `primary`. For a
   * host that is itself painted in `primary` (a selected tile), where the
   * plain sticker's ink is the host's fill.
   */
  inverted?: boolean;
  /**
   * Presence only: an 8px disc with no digit, seated 2px inside the corner.
   * The count still names the sticker for assistive tech.
   */
  dot?: boolean;
  className?: string;
};

/**
 * A count riding the top-right corner of a `position: relative` host: unread
 * items waiting at a destination the host stands for, or the active count on
 * an icon-only chip. A count that describes the host's own state in words
 * (filters on this button) is content and goes inside it.
 *
 * ONE colour rule: the disc is the host's INK and the digit its FILL —
 * `foreground` on `background`, which the surface scope rebinds to the host's
 * own pair — never the accent. A theme solves `primary` for white text ON it,
 * which makes it a mid-tone by construction, and a mid-tone is the quietest
 * thing on a near-black or near-white ground (Lc 29 on an indigo accent, 0 on
 * a navy one); foreground-on-surface is the one pair every theme must solve,
 * so the sticker inherits Lc 86+ in every cell. Zero chroma also keeps it
 * from reading as a fragment of the selected tile on a neutral-accent theme.
 *
 * ONE size rule: the disc is half the host's rung. Every Button publishes
 * `--button-h`, so a 24px toolbar chip wears a 12px disc, a 32px control a
 * 16px one and a 40px touch control a 20px one; a host that publishes no rung
 * (a rail tile) gets the 16px disc. The digit scales with it, and the disc
 * hangs an eighth of the rung past the host's top-end corner — 4px on a
 * 32px host, so its centre sits inside the corner; 3px on a 24px chip, inside
 * the 4px gap a toolbar leaves between chips. Zero padding: `min-w` alone
 * squares the disc, so a digit or a glyph renders a true circle.
 *
 * It rings itself in its OWN ink, not the ground's: the disc overhangs the
 * host, and a ground-coloured cutout dissolves wherever the overhang crosses
 * ground of the disc's own value. Ringed in its ink, one of the two always
 * closes the silhouette against whatever it overlaps. It stacks above later
 * siblings, so a joined neighbour never paints over it.
 *
 * The Badge positions ITSELF — no wrapper. A block wrapper carries the host's
 * line box, and the inline-flex disc sits on that box's baseline, below where
 * the wrapper was placed; the disc then hangs off the side but not the top.
 *
 * Keyed on `inverted` so the swap REMOUNTS the sticker: a host's fill snaps
 * between states, and easing the sticker's colours through the swap would
 * cross its fill and ink at the same grey mid-way — the digit blinks out.
 * Instead the outgoing sticker goes with the old state and the new one stamps
 * in a beat later (`animate-count-stamp`), already in its final colours.
 */
export function CountSticker({
  count,
  children,
  inverted = false,
  dot = false,
  className,
}: CountStickerProps): React.ReactElement {
  const paint = inverted
    ? "bg-primary-foreground text-primary ring-primary"
    : "bg-foreground text-background ring-background";
  if (dot) {
    return (
      <span
        key={inverted ? "inverted" : "plain"}
        aria-label={count}
        className={cn(
          "absolute -end-0.5 -top-0.5 size-2 rounded-full ring-[1.5px] motion-safe:animate-count-stamp",
          paint,
          className,
        )}
        data-slot="count-sticker"
        role="img"
      />
    );
  }
  return (
    <Badge
      key={inverted ? "inverted" : "plain"}
      className={cn(
        "pointer-events-none absolute z-1 font-semibold tabular-nums leading-none ring-[1.5px] motion-safe:animate-count-stamp",
        "-end-[calc(var(--button-h,2rem)/8)] -top-[calc(var(--button-h,2rem)/8)] h-[calc(var(--button-h,2rem)/2)] min-w-[calc(var(--button-h,2rem)/2)] px-0 text-[length:calc(var(--button-h,2rem)/4+.25rem)]",
        paint,
        className,
      )}
      aria-hidden
      data-slot="count-sticker"
      pill
      size="sm"
    >
      {children ?? count}
    </Badge>
  );
}
